import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { track, identifyUser } from "../lib/mixpanel";
import { sendLeadToCapi } from "../lib/metaCapi";
import styles from "../styles/consultsheet.module.css";
import { BranchStatus, getBranchRecruitmentStatus, fetchBranchRecruitmentStatus } from "../lib/branchrecruitmentstatus";
import { usePhoneVerify, VerifyBlock, PhoneVerifyTransport } from "./phoneverify";

/**
 * ConsultSheet — 상담·대기 신청 위저드.
 * claude.ai/design `consult.html` 5단계 위저드 이식:
 *   의도(intent) → 지점+상태(branch) → 정보·일정(form) → 연락처·인증(contact) → 완료(done)
 * - PC(≥760px): 중앙 모달(좌 카피 aside + 우 폼). 모바일(≤759px): 하단 바텀시트.
 * - 실제 API 연동(기존 consultsheet.tsx 그대로 이식):
 *     인증  GET/POST https://suneungsunbae.com/api/booking/cert
 *     상담시간 GET   https://suneungsunbae.com/api/booking
 *     상담제출 POST  https://suneungsunbae.com/api/booking/submit2
 *     대기제출 POST  https://peetsunbae.com/waiting/submit
 *   + mixpanel / fbq / Meta CAPI 추적은 성공 시 기존과 동일 페이로드로 유지.
 */

type Intent = "consult" | "waitlist";
type Step = "intent" | "branch" | "form" | "contact" | "done";
type Day = { year: number; month: number; date: number; day: number };

const BRANCHES: { id: string; name: string }[] = [
    { id: "daechi", name: "대치" }, { id: "gangnam", name: "강남" },
    { id: "songpa", name: "송파" }, { id: "bundang", name: "분당" },
    { id: "daegu", name: "수성" }, { id: "gimpo", name: "김포" },
    { id: "sinchon", name: "신촌" }, { id: "gangdong", name: "강동" },
    { id: "hanam", name: "하남" }, { id: "centum", name: "센텀" },
    { id: "dongtan", name: "동탄" },
];

const BADGE_LABEL: { [key in BranchStatus]: string } = {
    full: "대기 등록", soon: "마감 임박", open: "바로 등원",
};

const AMPM = [600, 630, 660, 690, 720, 750, 780, 810, 840, 870, 900, 930, 960, 990, 1020, 1050, 1080, 1110, 1140, 1170, 1200, 1230, 1260, 1290];
const WD = ["일", "월", "화", "수", "목", "금", "토"];

const minToLabel = (m: number) => `${Math.floor(m / 60)}:${m % 60 === 0 ? "00" : "30"}`;
const pad2 = (n: number) => (n < 10 ? "0" + n : "" + n);

type PopPos = { left: number; width: number; top?: number; bottom?: number };

// 달력·시간 팝오버 배치 (MiniCalendar/TimePicker 공용).
// 모바일(<760px)·단독 페이지: 팝업 하단을 같은 스텝의 '다음' CTA 상단에서 24px 위에 고정.
// PC 팝업: 트리거 아래(공간이 없으면 위)에 띄우는 기존 로직.
// CSS 모듈 해시 클래스명엔 '+' 같은 선택자 특수문자가 들어갈 수 있어 CSS.escape로 감싼다.
const cls = (name: string) => `.${typeof CSS !== "undefined" && CSS.escape ? CSS.escape(name) : name}`;
const placePopover = (trigger: HTMLElement): PopPos => {
    const r = trigger.getBoundingClientRect();
    const width = Math.max(r.width, 300);
    let left = r.left;
    if (left + width > window.innerWidth - 12) left = window.innerWidth - 12 - width;
    if (left < 12) left = 12;
    if (window.innerWidth < 760 || trigger.closest(cls(styles.standalone))) {
        const cta = trigger.closest(cls(styles.step))?.querySelector(cls(styles.cta));
        if (cta) {
            const bottom = Math.max(12, window.innerHeight - (cta.getBoundingClientRect().top - 24));
            return { left, width, bottom };
        }
    }
    const estH = 360;
    let top = r.bottom + 6;
    if (top + estH > window.innerHeight - 12) {
        const above = r.top - 6 - estH;
        top = above > 12 ? above : Math.max(12, window.innerHeight - 12 - estH);
    }
    return { left, width, top };
};

// 스크롤이 트리거 위치를 움직일 수 있는 컨테이너(조상)에서 발생했는지 —
// 모달 뒤 자동 스크롤 마퀴·팝오버 내부 스크롤은 무시한다.
const scrollMovesTrigger = (e: Event, trigger: HTMLElement | null) =>
    !!trigger && e.target instanceof Node && e.target.contains(trigger);

type TimeRow = { time: number; ampm: "am" | "pm"; possible: boolean; isPast: boolean };

/* ───────────────────────── 아이콘 ───────────────────────── */
const CalIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3.75" y="5.75" width="16.5" height="14.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M3.75 9.75h16.5M8 3.5v4M16 3.5v4" stroke="currentColor" strokeWidth="1.5" />
    </svg>
);
const ClockIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const CheckLi: React.FC = () => (
    <svg className={styles.asideChk} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12.5l4 4L19 7" />
    </svg>
);
const BackIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const HeadsetIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" />
        <path d="M21 16v2a4 4 0 0 1-4 4h-5" />
    </svg>
);

/* ───────────────────────── 미니 달력 (필드에 붙는 팝오버) ───────────────────────── */
const MiniCalendar: React.FC<{
    value: Day | null;
    onPick: (d: Day) => void;
    placeholder?: string;
    maxDate?: Date | null;
    error?: boolean;
}> = ({ value, onPick, placeholder = "날짜 선택", maxDate, error }) => {
    const todayRef = useRef<Date>((() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })());
    const today = todayRef.current;
    const [open, setOpen] = useState(false);
    const [view, setView] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<PopPos | null>(null);

    const place = () => {
        const t = triggerRef.current;
        if (t) setPos(placePopover(t));
    };

    useEffect(() => {
        if (!open) return;
        place();
        const onDoc = (e: MouseEvent) => {
            if (popRef.current && !popRef.current.contains(e.target as Node) &&
                triggerRef.current && !triggerRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onScroll = (e: Event) => { if (scrollMovesTrigger(e, triggerRef.current)) setOpen(false); };
        const onResize = () => setOpen(false);
        document.addEventListener("mousedown", onDoc);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const days = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const first = new Date(view.getFullYear(), view.getMonth(), 1).getDay();
    const prevDisabled = view.getFullYear() === today.getFullYear() && view.getMonth() === today.getMonth();

    const cells: React.ReactNode[] = [];
    for (let i = 0; i < first; i++) cells.push(<span key={"b" + i} />);
    for (let d = 1; d <= days; d++) {
        const dt = new Date(view.getFullYear(), view.getMonth(), d);
        const disabled = dt < today || (maxDate ? dt > maxDate : false);
        const isToday = dt.getTime() === today.getTime();
        const isSel = !!value && value.year === dt.getFullYear() && value.month === dt.getMonth() && value.date === d;
        cells.push(
            <button
                key={d}
                type="button"
                disabled={disabled}
                className={`${styles.calDay} ${isToday ? styles.isToday : ""} ${isSel ? styles.isSelected : ""}`}
                onClick={() => { onPick({ year: dt.getFullYear(), month: dt.getMonth(), date: d, day: dt.getDay() }); setOpen(false); }}
            >{d}</button>
        );
    }

    const label = value
        ? `${value.year}년 ${value.month + 1}월 ${value.date}일 (${WD[value.day]})`
        : placeholder;

    return (
        <div className={styles.cal}>
            <button
                ref={triggerRef}
                type="button"
                className={`${styles.calInput} ${error ? styles.isError : ""}`}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
            >
                <span className={`${styles.calValue} ${value ? "" : styles.isPlaceholder}`}>{label}</span>
                <CalIcon />
            </button>
            {open && pos && ReactDOM.createPortal(
                <div ref={popRef} className={styles.calPop} style={{ position: "fixed", left: pos.left, top: pos.top ?? "auto", bottom: pos.bottom ?? "auto", width: pos.width, right: "auto", zIndex: 3000, maxHeight: "min(72vh, 420px)", overflowY: "auto" }}>
                    <div className={styles.calHead}>
                        <button type="button" className={styles.calNav} disabled={prevDisabled} aria-label="이전 달"
                            onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}>‹</button>
                        <span className={styles.calMonth}>{view.getFullYear()}년 {view.getMonth() + 1}월</span>
                        <button type="button" className={styles.calNav} aria-label="다음 달"
                            onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}>›</button>
                    </div>
                    <div className={styles.calGrid}>
                        {WD.map((w) => <span key={w} className={styles.calWd}>{w}</span>)}
                        {cells}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

/* ───────────────────────── 시간 선택 (필드에 붙는 팝오버) ───────────────────────── */
const TimePicker: React.FC<{
    value: number | null;
    rows: TimeRow[];
    loading: boolean;
    noDate?: boolean;
    onPick: (t: number) => void;
    error?: boolean;
}> = ({ value, rows, loading, noDate, onPick, error }) => {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<PopPos | null>(null);

    const place = () => {
        const t = triggerRef.current;
        if (t) setPos(placePopover(t));
    };

    useEffect(() => {
        if (!open) return;
        place();
        const onDoc = (e: MouseEvent) => {
            if (popRef.current && !popRef.current.contains(e.target as Node) &&
                triggerRef.current && !triggerRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onScroll = (e: Event) => { if (scrollMovesTrigger(e, triggerRef.current)) setOpen(false); };
        const onResize = () => setOpen(false);
        document.addEventListener("mousedown", onDoc);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const am = rows.filter((r) => r.ampm === "am");
    const pm = rows.filter((r) => r.ampm === "pm");
    const Chips: React.FC<{ list: TimeRow[] }> = ({ list }) => (
        <div className={styles.times}>
            {list.map((r) => (
                <button
                    key={r.time}
                    type="button"
                    disabled={!(r.possible && !r.isPast)}
                    className={`${styles.timesChip} ${value === r.time ? styles.isActive : ""}`}
                    onClick={() => { onPick(r.time); setOpen(false); }}
                >{minToLabel(r.time)}</button>
            ))}
        </div>
    );

    return (
        <div className={styles.cal}>
            <button
                ref={triggerRef}
                type="button"
                className={`${styles.calInput} ${error ? styles.isError : ""}`}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
            >
                <span className={`${styles.calValue} ${value != null ? "" : styles.isPlaceholder}`}>{value != null ? minToLabel(value) : "시간 선택"}</span>
                <ClockIcon />
            </button>
            {open && pos && ReactDOM.createPortal(
                <div ref={popRef} className={styles.calPop} style={{ position: "fixed", left: pos.left, top: pos.top ?? "auto", bottom: pos.bottom ?? "auto", width: pos.width, right: "auto", zIndex: 3000, maxHeight: "min(72vh, 420px)", overflowY: "auto" }}>
                    {noDate ? (
                        <p className={styles.timesEmpty}>상담 날짜를 먼저 선택해 주세요</p>
                    ) : loading ? (
                        <p className={styles.timesEmpty}>가능한 시간을 불러오는 중…</p>
                    ) : (
                        <>
                            <p className={styles.timesCap}>오전</p>
                            {am.some((r) => r.possible && !r.isPast) ? <Chips list={am} /> : <p className={styles.timesEmpty}>예약 가능한 오전 시간이 없어요</p>}
                            <p className={styles.timesCap}>오후</p>
                            {pm.some((r) => r.possible && !r.isPast) ? <Chips list={pm} /> : <p className={styles.timesEmpty}>예약 가능한 오후 시간이 없어요</p>}
                        </>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

/* ───────────────────────── 번호 인증 (components/phoneverify.tsx로 추출, 전송부만 여기서 주입) ───────────────────────── */
const bookingCertTransport: PhoneVerifyTransport = {
    requestCert: (d) => {
        // 기존 동작 유지 — 발송은 fire-and-forget, 항상 성공으로 간주
        fetch(`https://suneungsunbae.com/api/booking/cert?telephoneNumber=${d}`, { method: "GET" })
            .then((r) => r.json()).then((res) => console.log(res)).catch(() => {});
        return Promise.resolve({ ok: true });
    },
    checkCert: (d, code) =>
        fetch(`https://suneungsunbae.com/api/booking/cert`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ telephoneNumber: d, certNumber: code }),
        }).then((r) => r.json()).then((res) => ({ ok: res.message === "success" })),
};

/* ───────────────────────── 스텝 헤더 (뒤로 + 닫기 X 한 행) ───────────────────────── */
const StepHead: React.FC<{ onBack?: () => void; backLabel?: string; onClose: () => void }> = ({ onBack, backLabel, onClose }) => (
    <div className={styles.stepHead}>
        {onBack ? (
            <button type="button" className={styles.back} onClick={onBack}><BackIcon /><span>{backLabel}</span></button>
        ) : (
            <span className={styles.headSpacer} />
        )}
        <button type="button" className={styles.close} aria-label="닫기" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
    </div>
);

/* ───────────────────────── aside 카피 (의도별) ───────────────────────── */
const ASIDE: { [k in Intent]: { title: React.ReactNode; points: string[] } } = {
    consult: {
        title: <>원하는 시간에<br />편하게 상담받으세요</>,
        points: [
            "상담실장이 예약하신 시간에 직접 연락드려요",
            "마감된 지점도 상담 후 자리를 잡아드려요",
            "결정 전이어도 부담 없이 신청하세요",
        ],
    },
    waitlist: {
        title: <>자리가 나면<br />가장 먼저 연락드려요</>,
        points: [
            "대기 등록 순서대로 자리를 우선 배정해 드려요",
            "자리가 나는 즉시 전화·알림톡으로 안내드려요",
            "대기 현황은 언제든 편하게 문의하세요",
        ],
    },
};

/* 의도 × 상태 안내 노트 — locked(단일 목적 링크 페이지)면 상담↔대기 전환 유도 제거 */
function BranchNote({ intent, status, onSwitch, locked }: { intent: Intent; status: BranchStatus; onSwitch: () => void; locked?: boolean }) {
    let cls = styles.noteOpen;
    let body: React.ReactNode = null;
    let canSwitch = false;
    if (intent === "consult") {
        if (status === "full") { cls = styles.noteFull; body = <>지금 정원이 차 있는 지점이에요. <b>상담 후 대기를 걸어</b>드릴게요.</>; }
        else if (status === "soon") { cls = styles.noteSoon; body = <><b>곧 마감</b>되는 지점이에요. 지금 상담받으면 대기 없이 바로 등원하실 수 있어요.</>; }
        else { cls = styles.noteOpen; body = <><b>바로 등원 가능</b>한 지점이에요. 상담에서 시작 시기만 맞춰드릴게요.</>; }
    } else {
        if (status === "open") { cls = styles.noteOpen; body = <>이 지점은 지금 <b>바로 등원 가능</b>해요. 대기 없이 바로 등록하실 수도 있어요.</>; canSwitch = true; }
        else if (status === "soon") { cls = styles.noteSoon; body = <><b>곧 마감</b>되는 지점이에요. 지금이라면 대기 없이 바로 등원하실 수도 있어요.</>; canSwitch = true; }
        else { cls = styles.noteFull; body = <>현재 정원이 차 있어요. 자리가 나면 <b>등록 순서대로</b> 가장 먼저 연락드릴게요.</>; }
    }
    if (locked) canSwitch = false;
    return (
        <div className={`${styles.note} ${cls}`}>
            <span>
                {body}
                {canSwitch && (
                    <button type="button" className={styles.noteSwitch} onClick={onSwitch}>상담부터 받아볼래요 →</button>
                )}
            </span>
        </div>
    );
}

/* ───────────────────────── 메인 컴포넌트 ───────────────────────── */
const ConsultSheet: React.FC<{
    open: boolean;
    onClose: () => void;
    /** 전용 링크 페이지 모드 — 오버레이가 아닌 전체 페이지로 렌더 (디자인 consult.html is-standalone). */
    standalone?: boolean;
    /** 의도 고정 — intent 선택 화면을 건너뛰고 지점 선택부터 시작. */
    lockedIntent?: Intent;
    /** ?campus= 지점 사전 선택 (BRANCHES id: daechi, gangnam, ...). lockedIntent와 함께만 적용. */
    initialCampus?: string;
    /** standalone 헤더의 헤드셋 버튼 — 빠른상담 시트 열기. */
    onQuick?: () => void;
}> = ({ open, onClose, standalone, lockedIntent, initialCampus, onQuick }) => {
    const [step, setStep] = useState<Step>(lockedIntent ? "branch" : "intent");
    const [intent, setIntent] = useState<Intent>(lockedIntent ?? "consult");
    const [submitting, setSubmitting] = useState(false);
    const [doneTitle, setDoneTitle] = useState("");
    const [doneSub, setDoneSub] = useState("");
    const [errs, setErrs] = useState<{ [k: string]: boolean }>({});

    const [campus, setCampus] = useState("");
    const [name, setName] = useState("");

    // 지점별 모집 상태 — App 로드 시점에 시작된 fetch의 캐시를 동기로 읽어 첫 오픈부터 깜빡임 없음
    const [waitStatus, setWaitStatus] = useState(getBranchRecruitmentStatus());
    useEffect(() => {
        let alive = true;
        fetchBranchRecruitmentStatus().then((m) => { if (alive) setWaitStatus(m); });
        return () => { alive = false; };
    }, []);

    // 상담 전용
    const [cSelect, setCSelect] = useState(0); // 0=방문, 1=전화
    const [cDate, setCDate] = useState<Day | null>(null);
    const [cTime, setCTime] = useState<number | null>(null);
    const [cTimes, setCTimes] = useState<TimeRow[]>([]);
    const [cTimesLoading, setCTimesLoading] = useState(false);

    // 대기 전용
    const [wGender, setWGender] = useState("male");
    const [wGrade, setWGrade] = useState("n");
    const [wEnroll, setWEnroll] = useState<Day | null>(null);
    const [wEtc, setWEtc] = useState("");

    // 단일 인증 훅 — 상담↔대기 분기 전환에도 인증 상태를 유지(디자인 consult.html과 동일).
    const v = usePhoneVerify(() => (intent === "consult" ? "consultation" : "waiting"), bookingCertTransport);

    const plus7 = useRef<Date>((() => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 7); return d; })()).current;

    const resetAll = useCallback(() => {
        setCampus(""); setName("");
        setCSelect(0); setCDate(null); setCTime(null); setCTimes([]); setCTimesLoading(false);
        setWGender("male"); setWGrade("n"); setWEnroll(null); setWEtc("");
        v.reset();
        setErrs({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 열릴 때 초기화 + body 스크롤 잠금 (사라지는 스크롤바 폭만큼 padding 보정 → 배경 안 밀림)
    useEffect(() => {
        const body = document.body;
        const restore = () => { body.style.overflow = ""; body.style.paddingRight = ""; };
        if (open) {
            // 링크형 단일 목적 폼: 의도 고정 → 두 갈래 선택 화면 건너뛰고 지점 선택부터 (디자인 lockedType).
            setStep(lockedIntent ? "branch" : "intent");
            setIntent(lockedIntent ?? "consult");
            resetAll();
            if (lockedIntent && initialCampus && BRANCHES.some((b) => b.id === initialCampus)) setCampus(initialCampus);
            const sw = window.innerWidth - document.documentElement.clientWidth;
            if (sw > 0) body.style.paddingRight = sw + "px";
            body.style.overflow = "hidden";
        } else {
            restore();
        }
        return restore;
    }, [open, resetAll, lockedIntent, initialCampus]);

    // ESC 닫기 — 단독 페이지에서는 ESC로 이탈하지 않음
    useEffect(() => {
        if (!open || standalone) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose, standalone]);

    // 상담 가능 시간 조회 (기존 로직 그대로)
    useEffect(() => {
        if (!open || intent !== "consult" || step !== "form" || !campus || !cDate) return;

        const location = campus;
        const select = cSelect;
        const day = cDate;
        const today = new Date();
        const currentTime = today.getHours() * 60 + today.getMinutes();
        const isToday = today.getFullYear() === day.year && today.getMonth() === day.month && today.getDate() === day.date;

        setCTimesLoading(true);
        let alive = true;
        fetch(`https://suneungsunbae.com/api/booking?select=${select}&year=${day.year}&month=${day.month}&date=${day.date}`, { method: "GET" })
            .then((r) => r.json())
            .then((result: any) => {
                if (!alive) return;
                const occupied: number[] = [];
                let selectedData: any;
                (result.axiosData || []).forEach((d: any) => { if (d.location === location && d.kind === select) selectedData = d; });
                (result.data || []).forEach((d: any) => {
                    if (location !== d.location) return;
                    if (d.name === "관리자") { if (d.kind === select) occupied.push(d.time); }
                    else occupied.push(d.time);
                });

                const rows: TimeRow[] = AMPM.map((t) => ({
                    time: t,
                    ampm: t <= 690 ? "am" : "pm",
                    possible: false,
                    isPast: isToday ? t < currentTime + 60 : false,
                }));

                if (selectedData) {
                    const isSat = day.day === 6, isSun = day.day === 0;
                    rows.forEach((row) => {
                        const sched = isSat ? selectedData.time.saturday : isSun ? selectedData.time.sunday : selectedData.time.weekdays;
                        if (sched && sched.includes(row.time)) row.possible = true;
                    });
                }

                const deadline = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7, 23, 59, 59).getTime();
                const selTime = new Date(day.year, day.month, day.date, 0, 0, 0).getTime();
                if (selTime > deadline) rows.forEach((row) => { row.possible = false; });

                rows.forEach((row) => { if (occupied.includes(row.time)) row.possible = false; });

                setCTimes(rows);
                setCTimesLoading(false);
            })
            .catch(() => { if (alive) { setCTimes([]); setCTimesLoading(false); } });

        return () => { alive = false; };
    }, [open, intent, step, campus, cSelect, cDate]);

    /* ── 단계 이동 ── */
    const formType = intent === "consult" ? "consultation" : "waiting";
    const chooseIntent = (i: Intent) => { setIntent(i); setCampus(""); setErrs({}); setStep("branch"); };
    const pickConsultDate = (d: Day) => { setCDate(d); setCTime(null); };
    const pickConsultType = (s: number) => {
        setCSelect(s); setCTime(null);
        track("Consultation Type Selected", { type: s === 0 ? "visit" : "phone" });
    };

    const goFromBranch = () => {
        if (!campus) { setErrs((p) => ({ ...p, campus: true })); return; }
        setStep("form");
    };

    const goFromForm = () => {
        const e: { [k: string]: boolean } = {};
        if (intent === "consult") {
            if (!cDate) e.cDate = true;
            if (!cTime) e.cTime = true;
        }
        setErrs(e);
        if (Object.keys(e).length) return;
        setStep("contact");
    };

    const submitConsult = () => {
        track("Consultation Form Submit Attempted", {
            location: campus, consultation_type: cSelect,
            has_name: !!name.trim(), has_time: cTime != null, cert_status: v.verified,
        });
        const e: { [k: string]: boolean } = {};
        if (!campus) e.campus = true;
        if (!cDate) e.cDate = true;
        if (cDate && !cTime) e.cTime = true;
        if (!name.trim()) e.name = true;
        if (v.phone.replace(/\D/g, "").length < 10) e.phone = true;
        if (!v.verified) e.cert = true;
        setErrs(e);
        if (Object.keys(e).length) { track("Consultation Form Validation Failed", { reason: Object.keys(e)[0] }); return; }

        setSubmitting(true);
        const data = {
            location: campus, select: cSelect, date: cDate, time: cTime,
            name: name, telephoneNumber: v.phone, cert: v.cert,
        };
        fetch(`https://suneungsunbae.com/api/booking/submit2`, {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ data }),
        }).then((r) => r.json()).then((result: any) => {
            setSubmitting(false);
            if (result.message === "fail" || result.message === "error") {
                track("Consultation Submitted Failed", { reason: result.message, location: campus, consultation_type: cSelect });
                alert("오류 관리자 문의(010-9880-0489)");
                return;
            }
            if (result.message === "success") {
                identifyUser(v.phone, {
                    "$name": name, "$phone": v.phone,
                    first_consultation_location: campus,
                    first_consultation_type: cSelect === 0 ? "visit" : "phone",
                    first_consultation_date: new Date().toISOString(),
                });
                track("Consultation Submitted", {
                    location: campus, consultation_type: cSelect, selected_time: cTime,
                    selected_date: cDate ? `${cDate.year}-${cDate.month + 1}-${cDate.date}` : "",
                });
                const eventId = `lead_consultation_${v.phone}_${Date.now()}`;
                if ((window as any).fbq) {
                    (window as any).fbq("track", "Lead", { content_name: "consultation_request", source: "consultation", value: 100000, currency: "KRW" }, { eventID: eventId });
                }
                sendLeadToCapi({ eventId, phone: v.phone, name: name, location: campus, consultationType: cSelect === 0 ? "visit" : "phone", contentName: "consultation_request" });
                setDoneTitle("상담 신청 완료!");
                setDoneSub(`${branchName(campus)} 지점 상담실장이 예약하신 시간에 연락드릴게요.`);
                setStep("done");
            }
        }).catch(() => { setSubmitting(false); alert("오류 관리자 문의(010-9880-0489)"); });
    };

    const submitWait = () => {
        const e: { [k: string]: boolean } = {};
        if (!campus) e.campus = true;
        if (!name.trim()) e.name = true;
        if (v.phone.replace(/\D/g, "").length < 10) e.phone = true;
        if (!v.verified) e.cert = true;
        setErrs(e);
        if (Object.keys(e).length) { track("Waiting List Validation Failed", { reason: Object.keys(e)[0] }); return; }

        setSubmitting(true);
        const enrollmentDate = wEnroll ? `${wEnroll.year}-${pad2(wEnroll.month + 1)}-${pad2(wEnroll.date)}` : "";
        const data = {
            location: campus, select: wGender, select2: wGrade,
            name: name, telephoneNumber: v.phone, cert: v.cert,
            etc: wEtc, enrollmentDate, year: 2026, type1: "winter26",
        };
        fetch(`https://peetsunbae.com/waiting/submit`, {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ data }),
        }).then((r) => r.json()).then((result: any) => {
            setSubmitting(false);
            if (result.message === "fail" || result.message === "error") {
                track("Waiting List Submit Failed", { reason: result.message, location: campus, form_type: "waiting_winter_2026" });
                alert("오류 관리자 문의(010-9880-0489)");
                return;
            }
            if (result.message === "success") {
                identifyUser(v.phone, {
                    "$name": name, "$phone": v.phone,
                    waiting_list_location: campus, student_type: wGrade, gender: wGender,
                    waiting_list_date: new Date().toISOString(),
                });
                track("Waiting List Submitted", { location: campus, gender: wGender, student_type: wGrade, form_type: "waiting_winter_2026" });
                const eventId = `lead_waiting_${v.phone}_${Date.now()}`;
                if ((window as any).fbq) {
                    (window as any).fbq("track", "Lead", { content_name: "waiting_request", source: "waitlist", value: 100000, currency: "KRW" }, { eventID: eventId });
                }
                sendLeadToCapi({ eventId, phone: v.phone, name: name, location: campus, contentName: "waiting_request" });
                setDoneTitle("대기 신청 완료!");
                setDoneSub(`${branchName(campus)} 지점에 자리가 나면, 등록 순서대로 가장 먼저 알려드릴게요.`);
                setStep("done");
            }
        }).catch(() => { setSubmitting(false); alert("오류 관리자 문의(010-9880-0489)"); });
    };

    const onSubmit = (e: React.FormEvent) => { e.preventDefault(); if (intent === "consult") submitConsult(); else submitWait(); };

    const status = campus ? waitStatus[campus] : null;

    // standalone 헤더 — 뒤로가기(스텝 역행, 지점 단계에선 페이지 이탈)와 3분절 진행선 (디자인 pagetop).
    const stepBack = () => {
        if (step === "contact") setStep("form");
        else if (step === "form") setStep("branch");
        else onClose();
    };
    const progLit = step === "branch" ? 1 : step === "form" ? 2 : 3;

    return (
        <div className={`${styles.sheet} ${open ? styles.isOpen : ""} ${step === "intent" ? styles.isIntent : ""} ${standalone ? styles.standalone : ""}`} aria-hidden={!open}>
            {!standalone && <div className={styles.scrim} onClick={onClose} />}
            {standalone && (
                <header className={styles.pagetop}>
                    <div className={styles.pagetopIn}>
                        {step !== "done" ? (
                            <button type="button" className={styles.pagetopBtn} aria-label="이전" onClick={stepBack}><BackIcon /></button>
                        ) : (
                            <span className={styles.pagetopSpacer} />
                        )}
                        <button type="button" className={styles.pagetopBtn} aria-label="빠른 상담" aria-haspopup="dialog" onClick={() => onQuick?.()}><HeadsetIcon /></button>
                        <div className={styles.pagetopProg} aria-hidden="true">
                            <span className={progLit >= 1 ? styles.isOn : ""} />
                            <span className={progLit >= 2 ? styles.isOn : ""} />
                            <span className={progLit >= 3 ? styles.isOn : ""} />
                        </div>
                    </div>
                </header>
            )}
            <div className={styles.panel} role="dialog" aria-modal={standalone ? undefined : true} aria-label="상담·대기 신청">
                {!standalone && <div className={styles.grip} />}

                {/* 좌측 카피 (PC 팝업, intent 단계 제외 — 단독 페이지에서는 없음) */}
                {!standalone && step !== "intent" && (
                    <aside className={styles.aside}>
                        <h2 className={styles.asideTitle}>{ASIDE[intent].title}</h2>
                        <ul className={styles.asidePoints}>
                            {ASIDE[intent].points.map((p, i) => (
                                <li key={i}><CheckLi /><span>{p}</span></li>
                            ))}
                        </ul>
                    </aside>
                )}

                <div className={styles.card}>
                    {/* STEP 1 — 의도 */}
                    {step === "intent" && (
                        <div className={`${styles.step} ${styles.stepIntent}`}>
                            <StepHead onClose={onClose} />
                            <h3 className={`${styles.q} ${styles.qLg}`}>상담·대기 신청을 <b>도와드릴게요</b></h3>
                            <p className={styles.qsub}>궁금한 점 무엇이든 편하게 남겨주세요.<br />망설여지면 대기만 걸어둬도 괜찮아요.</p>
                            <div className={styles.intents}>
                                <button type="button" className={styles.intent} onClick={() => chooseIntent("consult")}>
                                    <span className={styles.intentIco}><CalIcon /></span>
                                    <span className={styles.intentTxt}>
                                        <span className={styles.intentLab}>상담받고 정하기</span>
                                        <span className={styles.intentHint}>상담실장과 상담하고 지점·시간을 정해요</span>
                                    </span>
                                </button>
                                <button type="button" className={styles.intent} onClick={() => chooseIntent("waitlist")}>
                                    <span className={styles.intentIco}><ClockIcon /></span>
                                    <span className={styles.intentTxt}>
                                        <span className={styles.intentLab}>대기만 걸어두기</span>
                                        <span className={styles.intentHint}>자리가 나면 가장 먼저 연락드려요</span>
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* STEP 2 — 지점 + 상태 */}
                    {step === "branch" && (
                        <div className={styles.step}>
                            {!standalone && <StepHead onBack={() => setStep("intent")} backLabel="처음으로" onClose={onClose} />}
                            <h3 className={styles.q}>
                                {intent === "consult" ? <>어느 지점에서 <b>상담받으실래요?</b></> : <>어느 지점 <b>대기를 걸어둘까요?</b></>}
                            </h3>
                            <p className={styles.qsub}>
                                {intent === "consult"
                                    ? "어느 지점이든 상담받으실 수 있어요. 마감된 곳도 상담 후 자리를 잡아드려요."
                                    : "원하는 지점을 고르면 대기 순서에 올라가요. 자리가 나면 가장 먼저 연락드려요."}
                            </p>
                            <div className={`${styles.chipgrid} ${errs.campus ? styles.isError : ""}`}>
                                {BRANCHES.map((b) => {
                                    const st = waitStatus[b.id];
                                    const badgeCls = st === "full" ? styles.bFull : st === "soon" ? styles.bSoon : styles.bOpen;
                                    return (
                                        <button
                                            key={b.id}
                                            type="button"
                                            className={`${styles.chip} ${campus === b.id ? styles.isSelected : ""}`}
                                            onClick={() => { setCampus(b.id); setErrs((p) => ({ ...p, campus: false })); track("Location Selected", { location: b.id, form_type: formType }); }}
                                        >
                                            <span className={styles.chipName}>{b.name}</span>
                                            <span className={`${styles.chipBadge} ${badgeCls}`}>{BADGE_LABEL[st]}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className={styles.noteSlot}>
                                {status && <BranchNote intent={intent} status={status} locked={!!lockedIntent} onSwitch={() => setIntent("consult")} />}
                            </div>
                            <button type="button" className={styles.cta} disabled={!campus} onClick={goFromBranch}>
                                {intent === "consult" ? "상담 신청 계속하기" : "대기 신청 계속하기"}
                            </button>
                        </div>
                    )}

                    {/* STEP 3a — 정보·일정 */}
                    {step === "form" && (
                        <div className={styles.step}>
                            {!standalone && <StepHead onBack={() => setStep("branch")} backLabel="이전" onClose={onClose} />}
                            {!standalone && <div className={styles.prog}><span className={`${styles.progSeg} ${styles.isOn}`} /><span className={styles.progSeg} /></div>}
                            <h3 className={styles.q}>
                                {intent === "consult" ? <>상담 일정을 <b>정해주세요</b></> : <>기본 정보를 <b>알려주세요</b></>}
                            </h3>
                            <p className={styles.qsub}>{intent === "consult" ? "원하시는 방법과 날짜·시간을 골라주세요." : "몇 가지만 선택하면 돼요."}</p>

                            {intent === "consult" ? (
                                <>
                                    <div className={styles.field}>
                                        <label>상담 방법</label>
                                        <div className={styles.seg}>
                                            <button type="button" className={`${styles.segBtn} ${cSelect === 0 ? styles.isActive : ""}`} onClick={() => pickConsultType(0)}>방문 상담</button>
                                            <button type="button" className={`${styles.segBtn} ${cSelect === 1 ? styles.isActive : ""}`} onClick={() => pickConsultType(1)}>전화 상담</button>
                                        </div>
                                    </div>
                                    <div className={styles.field}>
                                        <label>상담 날짜</label>
                                        <MiniCalendar value={cDate} onPick={(d) => { pickConsultDate(d); setErrs((p) => ({ ...p, cDate: false })); }} maxDate={plus7} error={errs.cDate} />
                                    </div>
                                    <div className={styles.field}>
                                        <label>상담 시간</label>
                                        <TimePicker
                                            value={cTime}
                                            rows={cTimes}
                                            loading={cTimesLoading}
                                            noDate={!cDate}
                                            onPick={(t) => {
                                                setCTime(t); setErrs((p) => ({ ...p, cTime: false }));
                                                track("Time Slot Selected", { time: t, location: campus, consultation_type: cSelect, selected_date: cDate ? `${cDate.year}-${cDate.month + 1}-${cDate.date}` : "" });
                                            }}
                                            error={errs.cTime}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className={styles.field}>
                                        <label>성별</label>
                                        <div className={styles.seg}>
                                            <button type="button" className={`${styles.segBtn} ${wGender === "male" ? styles.isActive : ""}`} onClick={() => { setWGender("male"); track("Gender Selected", { gender: "male", form_type: formType }); }}>남학생</button>
                                            <button type="button" className={`${styles.segBtn} ${wGender === "female" ? styles.isActive : ""}`} onClick={() => { setWGender("female"); track("Gender Selected", { gender: "female", form_type: formType }); }}>여학생</button>
                                        </div>
                                    </div>
                                    <div className={styles.field}>
                                        <label>학년</label>
                                        <div className={styles.seg}>
                                            <button type="button" className={`${styles.segBtn} ${wGrade === "n" ? styles.isActive : ""}`} onClick={() => { setWGrade("n"); track("Student Type Selected", { student_type: "n_repeat", form_type: formType }); }}>N수생</button>
                                            <button type="button" className={`${styles.segBtn} ${wGrade === "current" ? styles.isActive : ""}`} onClick={() => { setWGrade("current"); track("Student Type Selected", { student_type: "enrolled", form_type: formType }); }}>재학생</button>
                                        </div>
                                    </div>
                                    <div className={styles.field}>
                                        <label>입학 희망일 <span className={styles.opt}>(선택)</span></label>
                                        <MiniCalendar value={wEnroll} onPick={setWEnroll} placeholder="날짜 선택" />
                                    </div>
                                    <div className={styles.field}>
                                        <label>기타 요청사항 <span className={styles.opt}>(선택)</span></label>
                                        <textarea className={styles.area} value={wEtc} onChange={(e) => setWEtc(e.target.value)} placeholder="문의하실 내용을 자유롭게 적어주세요." />
                                    </div>
                                </>
                            )}
                            <button type="button" className={styles.cta} onClick={goFromForm}>다음</button>
                        </div>
                    )}

                    {/* STEP 3b — 연락처·인증 */}
                    {step === "contact" && (
                        <div className={styles.step}>
                            {!standalone && <StepHead onBack={() => setStep("form")} backLabel="이전" onClose={onClose} />}
                            {!standalone && <div className={styles.prog}><span className={`${styles.progSeg} ${styles.isOn}`} /><span className={`${styles.progSeg} ${styles.isOn}`} /></div>}
                            <h3 className={styles.q}>연락처를 <b>알려주세요</b></h3>
                            <p className={styles.qsub}>
                                {intent === "consult"
                                    ? "상담실장이 직접 연락드려요. 결정 전이어도 편하게 신청하세요."
                                    : "자리가 나면 이 번호로 가장 먼저 연락드릴게요."}
                            </p>
                            <form onSubmit={onSubmit} noValidate>
                                <div className={styles.field}>
                                    <label>이름</label>
                                    <input type="text" placeholder="이름" value={name} onChange={(e) => { setName(e.target.value); setErrs((p) => ({ ...p, name: false })); }} className={errs.name ? styles.isError : ""} />
                                </div>
                                <div className={styles.field}>
                                    <label>번호 인증</label>
                                    <VerifyBlock v={v} phoneErr={errs.phone} certErr={errs.cert} />
                                </div>
                                <button type="submit" className={styles.cta} disabled={submitting}>
                                    {intent === "consult" ? "상담 신청하기" : "대기 신청하기"}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* STEP 4 — 완료 */}
                    {step === "done" && (
                        <div className={styles.step}>
                            {!standalone && <StepHead onClose={onClose} />}
                            <div className={styles.done}>
                                <div className={styles.check}>✓</div>
                                <h4>{doneTitle}</h4>
                                <p>{doneSub}</p>
                                <button type="button" className={styles.ghost} onClick={onClose}>확인</button>
                            </div>
                        </div>
                    )}
                </div>
                {/* 하단 고정 CTA 뒤 흰색 커버 스트립 (디자인 card::after) */}
                {standalone && step !== "done" && <div className={styles.ctaCover} aria-hidden="true" />}
            </div>
        </div>
    );
};

function branchName(id: string) {
    const b = BRANCHES.find((x) => x.id === id);
    return b ? b.name : "";
}

export default ConsultSheet;
